/**
 * 修改密码对话框（v0.47）。两种用法共用**同一个组件**：
 *
 * · 强制模式（`forced`）：管理员重置过密码 / 内置管理员首次登录 ⇒
 *   不给"取消"，改完才能进主界面。**必须能强制** —— 否则"首次登录要改密码"
 *   只是一句建议，内置管理员的初始密码就会一直有效。
 * · 普通模式：点顶栏「修改密码」。
 *
 * ★ 为什么不做成"登录页上的第二个表单"：改密码是**已登录**后的动作
 *   （`POST /api/auth/password` 要求带会话），挂在登录页就要为它单独开一次
 *   鉴权例外，等于在公开面上多凿一个口子。
 */
import { useState } from 'react';
import { changeMyPassword } from '../api/client';
import { useAuthStore } from '../store/auth';

interface Props {
  forced?: boolean;
  reason?: string;
  onClose?: () => void;
  onToast?: (msg: string, type?: 'info' | 'success' | 'error') => void;
}

export function PasswordDialog({ forced = false, reason, onClose, onToast }: Props) {
  const setUser = useAuthStore((s) => s.setUser);
  const [oldPw, setOldPw] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (busy) return;
    if (pw1 !== pw2) { setErr('两次输入的新密码不一致'); return; }
    if (pw1.length < 8) { setErr('新密码至少 8 位'); return; }
    setBusy(true);
    setErr(null);
    try {
      const res = await changeMyPassword(oldPw, pw1);
      setUser(res.user);
      setDone(true);
      onToast?.('密码已修改', 'success');
      if (!forced) onClose?.();
    } catch (e: any) {
      setErr(e?.message || '修改失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="job-overlay" onClick={() => { if (!forced) onClose?.(); }}>
      <div className="job-panel pwd-panel" onClick={(e) => e.stopPropagation()}>
        {!forced && (
          <button className="job-close" aria-label="关闭" onClick={onClose}>×</button>
        )}
        <h3 className="pwd-title">
          {forced ? '请先修改密码' : '修改密码'}
        </h3>
        {forced && (
          <div className="pwd-reason">
            {reason || '当前密码是管理员代设的，首次登录必须改成只有你知道的密码。'}
          </div>
        )}

        <label className="login-field">
          <span>{forced ? '当前密码' : '原密码'}</span>
          <input className="login-input" type="password" autoComplete="current-password"
                 value={oldPw} onChange={(e) => setOldPw(e.target.value)} />
        </label>
        <label className="login-field">
          <span>新密码（至少 8 位）</span>
          <input className="login-input" type="password" autoComplete="new-password"
                 value={pw1} onChange={(e) => setPw1(e.target.value)} />
        </label>
        <label className="login-field">
          <span>再输一次</span>
          <input className="login-input" type="password" autoComplete="new-password"
                 value={pw2} onChange={(e) => setPw2(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
        </label>

        {err && <div className="login-err">{err}</div>}
        {done && forced && (
          <div className="pwd-ok">密码已修改 —— 正在进入系统…</div>
        )}

        <div className="pwd-actions">
          {!forced && (
            <button className="btn" onClick={onClose} disabled={busy}>取消</button>
          )}
          <button className="btn primary" onClick={submit} disabled={busy}>
            {busy ? '提交中…' : '确认修改'}
          </button>
        </div>
      </div>
    </div>
  );
}
